<template>
  <div v-if="contactSuccess || isMagnoliaEdit">
    <EditableArea :content="successArea" :custom-view="FeedbackArea" />

    <NewsletterStatusPanel
      v-if="isMagnoliaEdit || newsletterSubmitted"
      :background-color="backgroundColor"
      :dot-pattern="dotPattern"
      :headline="confirmationHeadline"
      :subline="confirmationSubline"
      :image="image"
      :image-first="imageFirst"
      :steps="confirmationSteps"
    >
      <template #editorialNotification>
        <div
          v-if="isMagnoliaEdit"
          :class="
            newsletterActivated
              ? 'editmode-info-box bg-green text-green'
              : 'editmode-warning-box bg-red-500 text-red-500'
          "
          class="w-min rounded-br-2xl p-2.5 font-medium"
        >
          <span v-if="newsletterActivated">[NEWSLETTER_IS_ACTIVATED]</span>
          <span v-else>[NEWSLETTER_IS_DEACTIVATED]</span>
          <span v-if="newsletterActivated && !interactionId" class="p-1 text-red-500"
            >[INTERACTION_ID_MISSING]</span
          >
        </div></template
      >
    </NewsletterStatusPanel>
  </div>
  <form
    v-if="!contactSuccess || isMagnoliaEdit"
    class="contact-form-component relative"
    @submit.prevent="onSubmit()"
  >
    <LoadingElement v-if="isLoading" class="bg-white/70" overlay="true" />

    <div class="xl:responsive-gap relative grid gap-y-6 xl:grid-cols-12">
      <div class="flex flex-col gap-4 md:gap-6 xl:col-span-5">
        <Heading v-if="primaryHeadline" class="mb-2" size="small" tag="h3">
          {{ primaryHeadline }}
        </Heading>
        <Heading v-else class="mb-2 hidden xl:block">&nbsp;</Heading>

        <SelectField
          v-if="isMagnoliaEdit || selectableReasons.length > 1"
          v-model="formState.subject"
          :error-message="contactFormVuelidate?.subject?.$errors?.[0]?.$message"
          :label="contactFormLabels?.['subject'] ?? 'subject'"
          :options="selectableReasons"
          :placeholder="
            contactFormPlaceholder?.['subject'] ?? contactFormLabels?.['subject'] ?? 'subject'
          "
          :required="formRules?.subject?.hasOwnProperty('required')"
          class="form-field-grey"
          name="subject"
          @blur="contactFormVuelidate?.subject?.$touch()"
        />

        <TextAreaField
          v-model="formState.message"
          :error-message="contactFormVuelidate?.message?.$errors?.[0]?.$message"
          :label="contactFormLabels?.['message'] ?? 'message'"
          :maxlength="messageMaxLength"
          :placeholder="
            contactFormPlaceholder?.['message'] ?? contactFormLabels?.['message'] ?? 'message'
          "
          :required="formRules?.message?.hasOwnProperty('required')"
          name="message"
          @blur="contactFormVuelidate?.message?.$touch()"
        />
      </div>
      <div class="flex flex-col gap-4 md:gap-6 xl:col-span-6 xl:col-start-7">
        <Heading v-if="secondaryHeadline" class="mb-2" size="small" tag="h3">
          {{ secondaryHeadline }}
        </Heading>
        <Heading v-else class="mb-2 hidden xl:block">&nbsp;</Heading>
        <div
          v-if="isMagnoliaEdit && selectableCountries.length === 0"
          class="flex rounded-xl bg-red-500 p-4"
        >
          <span class="m-auto font-medium">[COUNTRY_IS_MISSING_IN_DIALOG]</span>
        </div>
        <div class="grid gap-4 md:grid-cols-2 md:gap-6">
          <template v-for="(value, key) in formState" :key="key">
            <!-- filter primary fields-->
            <template v-if="!['message', 'subject', 'country'].includes(key)">
              <template v-if="key === 'salutation'">
                <SelectField
                  v-model="formState[key]"
                  :error-message="contactFormVuelidate?.[key]?.$errors?.[0]?.$message"
                  :label="contactFormLabels?.[key] ?? key"
                  :options="selectableSalutations"
                  :placeholder="contactFormPlaceholder?.[key] ?? contactFormLabels?.[key] ?? key"
                  :required="formRules[key]?.hasOwnProperty('required')"
                  class="form-field-grey col-start-1"
                  @blur="contactFormVuelidate?.[key]?.$touch()"
                />
              </template>
              <InputField
                v-else
                v-model="formState[key]"
                :autocomplete="['middlename'].includes(key) ? 'off' : null"
                :class="{
                  'col-start-1': ['company', 'first_name', 'salutation'].includes(key),
                  'form-field-umbrella': ['middlename'].includes(key),
                }"
                :error-message="contactFormVuelidate?.[key]?.$errors?.[0]?.$message"
                :label="contactFormLabels?.[key] ?? key"
                :min="fieldTypes[key] === 'date' ? minDate : null"
                :name="key"
                :placeholder="contactFormPlaceholder?.[key] ?? contactFormLabels?.[key] ?? key"
                :required="formRules?.[key]?.hasOwnProperty('required')"
                :type="fieldTypes[key] ?? 'text'"
                class="form-field-grey"
                @blur="contactFormVuelidate?.[key]?.$touch()"
              />
            </template>
          </template>
          <SelectField
            v-if="isMagnoliaEdit || selectableCountries.length > 1"
            v-model="formState.country"
            :label="contactFormLabels?.['country'] ?? 'country'"
            :options="selectableCountries"
            :placeholder="
              contactFormPlaceholder?.['country'] ?? contactFormLabels?.['country'] ?? 'country'
            "
            class="form-field-grey"
            name="country"
            required
            @blur="contactFormVuelidate?.country?.$touch()"
          />
        </div>
        <div class="prose prose-sm prose-black max-w-none hyphens-auto">
          <CommonLabel path="/BWF/form/shared/requiredFieldLabel" />
          <div class="mt-2">
            <CommonLabel path="/BWF/form/shared/privacyLinkLabel" />
          </div>
        </div>
      </div>
      <div v-if="showNewsletterRegistration" class="col-span-full">
        <Heading v-if="newsletterHeadline" size="small" tag="h2">
          {{ newsletterHeadline }}
        </Heading>
        <Checkbox
          v-model="newsletter.newsletterConsent"
          class="mt-4 text-sm"
          :error-text="newsletterVuelidate?.newsletterConsent?.$errors?.[0]?.$message"
          :error="!!newsletterVuelidate?.newsletterConsent?.$errors?.[0]?.$message"
          @blur="newsletterVuelidate.newsletterConsent?.$touch()"
        >
          <RichTextComponent v-if="consentCheckboxText" :rich-text="consentCheckboxText" />
          <CommonLabel v-else path="/BWF/components/newsletter/newsletterConsent" />
        </Checkbox>
      </div>
      <VueFriendlyCaptcha
        ref="captcha"
        :sitekey="siteKey"
        :language="currentLanguageShort"
        @done="
          (solution) => {
            captchaSolution = solution;
          }
        "
      />

      <button
        :disabled="deactivateButton || !captchaSolution"
        class="btn btn-blue col-span-full place-self-start"
        type="submit"
      >
        <CommonLabel path="BWF/form/contact/label/sendButtonLabel" />
      </button>
    </div>
  </form>
</template>

<script setup>
import { EditableArea } from "@magnolia/vue-editor";
import VueFriendlyCaptcha from "@somushq/vue3-friendly-captcha";
import { useVuelidate } from "@vuelidate/core";
import { Checkbox } from "~/components/clarity-ui/Checkbox";
import { Heading } from "~/components/clarity-ui/Heading";
import useBritaMiddleware from "~/composables/useBritaMiddleware";
import useCaptcha from "~/composables/useCaptcha";
import FeedbackArea from "~/templates/areas/FeedbackArea.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import LoadingElement from "~/templates/elements/LoadingElement.vue";
import SelectField from "~/templates/elements/form/SelectField.vue";
import TextAreaField from "~/templates/elements/form/TextAreaField.vue";
import InputField from "~/templates/elements/form/InputField.vue";
import RichTextComponent from "~/templates/components/cms/RichTextComponent.vue";
import NewsletterStatusPanel from "~/templates/elements/NewsletterStatusPanel.vue";

const props = defineProps([
  "confirmationHeadline",
  "confirmationSubline",
  "confirmationSteps",
  "backgroundColor",
  "dotPattern",
  "image",
  "imageFirst",
  "successArea",
  "commonLabel",
  "selectableCountries",
  "visible",
  "required",
  "messageMaxLength",
  "primaryHeadline",
  "secondaryHeadline",
  "confirmModalArea",
  "errorModalArea",
  "newsletterHeadline",
  "newsletterActivated",
  "newsletterConsentRequired",
  "consentCheckboxText",
  "interactionId",
  "actionId",
]);

const { isDesktop, isTabletOrMobile } = useDevice();

const { pushError, pushSuccess } = useNotifications();

const { user, isLoggedIn } = useUser();
const { contactForm, register, getDoiState } = useBritaMiddleware();
const { getLabels, getLabelByPath } = useCommonLabels();
const { currentLanguage, currentLanguageShort } = useMagnoliaLanguage();
const { isMagnoliaEdit } = useMagnoliaContext();

const captcha = ref();
const { captchaSolution, siteKey } = useCaptcha();

const contactSuccess = ref(false);
const isLoading = ref(false);

const selectableReasons =
  props.commonLabel?.map(({ name, value }) => ({
    value: name,
    label: value,
  })) ?? [];

const selectableCountries =
  props.selectableCountries?.map(({ name, value }) => ({
    value: name,
    label: value,
  })) ?? [];

const today = new Date();
const minDate = today.toISOString().slice(0, -14);

const fieldTypes = { language: "hidden", email: "email", cancellation_date: "date" };
// Because of a Frontend glitch, we fill the salutation field in onMounted
const formState = reactive({
  subject: selectableReasons?.length === 1 ? selectableReasons?.[0]?.value : "",
  message: "",
  ...(props.visible?.includes("company") ? { company: "" } : {}),
  ...(props.visible?.includes("salutation") ? { salutation: "" } : {}),
  ...(props.visible?.includes("title") ? { title: "" } : {}),
  first_name: user.value?.firstName ?? "",
  last_name: user.value?.lastName ?? "",
  email: user.value?.email ?? "",
  ...(props.visible?.includes("contract_number") ? { contract_number: "" } : {}),
  ...(props.visible?.includes("cancellation_date") ? { cancellation_date: "" } : {}),
  ...(props.visible?.includes("phone") ? { phone: "" } : {}),
  ...(props.visible?.includes("street_number") ? { street_number: "" } : {}),
  ...(props.visible?.includes("address_additional_info") ? { address_additional_info: "" } : {}),
  ...(props.visible?.includes("post_code") ? { post_code: "" } : {}),
  ...(props.visible?.includes("city") ? { city: "" } : {}),
  country: selectableCountries?.length === 1 ? selectableCountries?.[0]?.value : "",
  ...(props.visible?.includes("state") ? { state: "" } : {}),
  // TODO figure out what to do with theses ?
  language: currentLanguage.value,
  middlename: "",
});

const {
  firstNameValidators,
  lastNameValidators,
  emailValidators,
  streetNumberValidators,
  addressAdditionalValidators,
  cityValidators,
  phoneValidators,
  zipCodeValidators,
  requiredValidator,
  messageValidators,
  contractNumberValidators,
  consentValidators,
} = await useValidation();

// merge with mgnl config
const formRules = computed(() => ({
  subject: {
    ...requiredValidator,
  },
  email: {
    ...requiredValidator,
    ...emailValidators,
  },
  last_name: {
    ...requiredValidator,
    ...lastNameValidators,
  },
  first_name: {
    ...requiredValidator,
    ...firstNameValidators,
  },
  message: {
    ...requiredValidator,
    ...messageValidators,
  },
  post_code: {
    ...(props.visible?.includes("post_code") && props.required?.includes("post_code")
      ? { ...requiredValidator }
      : {}),
    ...zipCodeValidators,
  },
  contract_number: {
    ...contractNumberValidators,
    ...(props.visible?.includes("contract_number") && props.required?.includes("contract_number")
      ? { ...requiredValidator }
      : {}),
  },
  cancellation_date: {
    ...(props.visible?.includes("cancellation_date") &&
    props.required?.includes("cancellation_date")
      ? { ...requiredValidator }
      : {}),
  },
  street_number: {
    ...(props.visible?.includes("street_number") && props.required?.includes("street_number")
      ? { ...requiredValidator }
      : {}),
    ...streetNumberValidators,
  },
  address_additional_info: {
    ...(props.visible?.includes("address_additional_info") &&
    props.required?.includes("address_additional_info")
      ? { ...requiredValidator }
      : {}),
    ...addressAdditionalValidators,
  },
  city: {
    ...(props.visible?.includes("city") && props.required?.includes("city")
      ? { ...requiredValidator }
      : {}),
    ...cityValidators,
  },
  country: { ...requiredValidator },
  phone: {
    ...(props.visible?.includes("phone") && props.required?.includes("phone")
      ? { ...requiredValidator }
      : {}),
    ...phoneValidators,
  },
  ...(props.visible?.includes("salutation") && props.required?.includes("salutation")
    ? { salutation: { ...requiredValidator } }
    : {}),
  ...(props.visible?.includes("company") && props.required?.includes("company")
    ? { company: { ...requiredValidator } }
    : {}),
  ...(props.visible?.includes("state") && props.required?.includes("state")
    ? { state: { ...requiredValidator } }
    : {}),
  ...(props.visible?.includes("title") && props.required?.includes("title")
    ? { title: { ...requiredValidator } }
    : {}),
}));

// Build a flat Map of potential field labels and placeholders
const fieldLabelKeys = Object.keys(formState)
  .map((key) => key)
  .filter((key) => fieldTypes[key] !== "hidden");
//
const contactFormLabels = getLabels("/BWF/form/contact/label/", fieldLabelKeys);
const contactFormPlaceholder = getLabels("/BWF/form/contact/placeholder/", fieldLabelKeys);
const salutationLabels = getLabels("/BWF/form/shared/salutations/", [
  "not_specified",
  "mr",
  "mrs",
  "diverse",
]);
const selectableSalutations = [
  ...(Object.entries(salutationLabels ?? {})?.map(([key, value]) => ({
    value: key,
    label: value,
  })) ?? []),
];

const contactFormPushLabels = getLabels("BWF/form/contact/push/", [
  "newsletterSuccess",
  "newsletterError",
  "contactFormSuccess",
  "contactFormError",
]);

const zipCodePushLabel = getLabelByPath("BWF/shared/middleware/errors/zipCodeError");

const newsletter = reactive({
  newsletterConsent: null,
});
const newsletterRules = reactive({
  ...(props.newsletterConsentRequired ? { newsletterConsent: { ...consentValidators } } : {}),
});

const newsletterAlreadyRegistered = ref(false);
const newsletterSubmitted = ref(false);
const showNewsletterRegistration = computed(
  () => props?.newsletterActivated && (isMagnoliaEdit.value || !newsletterAlreadyRegistered.value),
);

const deactivateButton = ref(false);
const contactFormVuelidate = useVuelidate(formRules, formState);
const newsletterVuelidate = useVuelidate(newsletterRules, newsletter);
const onSubmit = async () => {
  deactivateButton.value = true;
  if (formState.salutation === "") {
    formState.salutation = selectableSalutations?.[0]?.value;
  }
  contactFormVuelidate.value.$touch();
  newsletterVuelidate.value.$touch();
  const valid = await contactFormVuelidate.value.$validate();
  const newsletterValid = await newsletterVuelidate.value.$validate();
  if (
    !valid ||
    (props.newsletterActivated &&
      props.newsletterConsentRequired &&
      !newsletterValid &&
      !newsletterAlreadyRegistered.value)
  ) {
    // show generic error or modal !?
    pushError(contactFormPushLabels?.contactFormError);
    deactivateButton.value = false;
    return;
  }
  try {
    isLoading.value = true;
    await contactForm({ ...formState, bwfCaptcha: captchaSolution.value });
    pushSuccess(contactFormPushLabels?.contactFormSuccess);
    contactSuccess.value = true;
    useContactformTracking(formState?.subject);
  } catch (e) {
    if (e.data === "postalcode_validation_failed") {
      pushError(zipCodePushLabel);
      deactivateButton.value = false;
    } else {
      pushError(contactFormPushLabels?.contactFormError);
      deactivateButton.value = false;
    }
    isLoading.value = false;
    return;
  }
  isLoading.value = false;
  if (newsletter?.newsletterConsent) {
    const doiState = await getDoiState(formState.email);
    if (doiState.mailDoiState !== "CONFIRMED") {
      try {
        await register(
          formState?.email,
          props?.interactionId,
          props?.actionId,
          formState?.middlename,
        );
        newsletterSubmitted.value = true;
      } catch (e) {
        pushError(contactFormPushLabels?.newsletterError);
      }
    }
  }
};

onMounted(async () => {
  if (!isMagnoliaEdit.value && isLoggedIn.value) {
    try {
      const doiState = await getDoiState(user.value?.email);
      newsletterAlreadyRegistered.value = doiState?.donotbulkemail === false;
    } catch (e) {
      console.error(e);
    }
  }

  // Because of a Frontend glitch, where the salutation label overlaps with the value, we must fill the salutation field after page mount. better solutions are welcome
  if (props.visible?.includes("salutation")) {
    formState.salutation = user.value?.salutation?.salutationKey ?? "";
  }
});
</script>
